import { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const MAX_IMAGES = 5;
const MAX_FILE_BYTES = 1.2 * 1024 * 1024; // ~1.2 MB to keep base64 under Mongo doc limit

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

function formatTs(iso) {
  if (!iso) return "";
  try {
    return iso.replace("T", " · ").slice(0, 16) + " UTC";
  } catch {
    return iso;
  }
}

function CaptureCard({ capture, isAdmin, onEdit, onDelete }) {
  const navigate = useNavigate();
  const openDetail = () => navigate(`/the-terra-chronicle/${capture.id}`);

  // Buttons inside the card stop propagation so they don't trigger the open.
  const stop = (fn) => (e) => { e.stopPropagation(); fn(); };

  return (
    <article
      role="button"
      tabIndex={0}
      onClick={openDetail}
      onKeyDown={(e) => { if (e.key === "Enter") openDetail(); }}
      className="border border-[#333] bg-[#0a0a0a] overflow-hidden cursor-pointer transition-colors hover:border-[#A3A3A3] focus:outline-none focus:border-[#E3E1D9]"
      data-testid={`terra-card-${capture.id}`}
    >
      {capture.images && capture.images.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-[#222]">
          {capture.images.map((src, i) => (
            <div key={`${src.slice(-24)}-${i}`} className="aspect-[4/3] bg-black overflow-hidden">
              <img src={src} alt={`capture-${i}`} className="w-full h-full object-cover tiv-img" />
            </div>
          ))}
        </div>
      ) : (
        <div className="aspect-[4/3] flex items-center justify-center text-[#444] text-xs tracking-widest uppercase">
          ::: no image filed :::
        </div>
      )}

      <div className="p-5 space-y-3">
        <div className="flex flex-wrap items-baseline gap-3">
          <span className="text-[0.55rem] tracking-[0.35em] uppercase text-[#A3A3A3] font-mono-data">
            {capture.id}
          </span>
          {capture.source_date && (
            <span className="text-[0.6rem] tracking-[0.3em] uppercase text-[#555]">
              {capture.source_date}
            </span>
          )}
          <span className="ml-auto text-[0.6rem] tracking-[0.3em] uppercase text-[#555] group-hover:text-[#E3E1D9]">
            open →
          </span>
        </div>
        {capture.headline ? (
          <h3 className="font-serif-display text-2xl leading-tight">{capture.headline}</h3>
        ) : (
          <h3 className="font-serif-display text-2xl leading-tight text-[#555] italic">[ untitled ]</h3>
        )}
        {capture.caption && (
          <p className="text-sm text-[#A3A3A3] leading-relaxed whitespace-pre-wrap line-clamp-3">{capture.caption}</p>
        )}
        {capture.tags && capture.tags.length > 0 && (
          <TagPills tags={capture.tags} />
        )}
        <div className="flex flex-wrap items-center justify-between gap-3 pt-3 border-t border-[#222]">
          <div className="text-[0.6rem] tracking-[0.25em] uppercase text-[#555]">
            filed by <span className="text-[#A3A3A3]">{capture.uploader_username || "—"}</span>
            {capture.uploader_alias && (
              <span className="text-[#555]"> · {capture.uploader_alias}</span>
            )}
          </div>
          <div className="text-[0.55rem] tracking-[0.25em] text-[#555] font-mono">
            {formatTs(capture.created_at)}
          </div>
        </div>
        {isAdmin && (
          <div className="flex gap-2 pt-2">
            <button className="tiv-btn text-[0.6rem]" onClick={stop(() => onEdit(capture))} data-testid={`terra-edit-${capture.id}`}>
              Amend
            </button>
            <button className="tiv-btn tiv-btn-danger text-[0.6rem]" onClick={stop(() => onDelete(capture))} data-testid={`terra-delete-${capture.id}`}>
              Burn
            </button>
          </div>
        )}
      </div>
    </article>
  );
}

function CaptureForm({ editing, draft, setDraft, onSave, onCancel, saving }) {
  const [tagInput, setTagInput] = useState("");

  const addTag = () => {
    const t = tagInput.trim().toLowerCase();
    if (!t) return;
    if ((draft.tags || []).includes(t)) {
      setTagInput("");
      return;
    }
    setDraft({ ...draft, tags: [...(draft.tags || []), t] });
    setTagInput("");
  };

  const removeTag = (t) => {
    setDraft({ ...draft, tags: (draft.tags || []).filter((x) => x !== t) });
  };

  const onTagKey = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addTag();
    }
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_FILE_BYTES) {
      toast.error("Image too large (~1.2MB max).");
      return;
    }
    if ((draft.images || []).length >= MAX_IMAGES) {
      toast.error(`At most ${MAX_IMAGES} images per dispatch.`);
      return;
    }
    const b64 = await fileToBase64(file);
    setDraft({ ...draft, images: [...(draft.images || []), b64] });
    e.target.value = "";
  };

  const removeImage = (i) => {
    setDraft({ ...draft, images: draft.images.filter((_, idx) => idx !== i) });
  };

  return (
    <div className="border border-[#333] bg-[#0a0a0a] p-6 space-y-5" data-testid="terra-form">
      <div className="text-[0.6rem] tracking-[0.4em] uppercase text-[#555]">
        {editing ? `Amending dispatch ${editing.id}` : "File new dispatch"}
      </div>

      <label className="block">
        <div className="text-[0.6rem] tracking-[0.3em] uppercase text-[#555] mb-1">Headline</div>
        <input
          className="tiv-input"
          value={draft.headline}
          onChange={(e) => setDraft({ ...draft, headline: e.target.value })}
          placeholder="story as it was filed in the surface press..."
          data-testid="terra-headline"
        />
      </label>

      <label className="block">
        <div className="text-[0.6rem] tracking-[0.3em] uppercase text-[#555] mb-1">Source date</div>
        <input
          className="tiv-input"
          value={draft.source_date}
          onChange={(e) => setDraft({ ...draft, source_date: e.target.value })}
          placeholder="MMXXVI.II.14 — Reuters / NYT / etc."
          data-testid="terra-source-date"
        />
      </label>

      <label className="block">
        <div className="text-[0.6rem] tracking-[0.3em] uppercase text-[#555] mb-1">Caption / note</div>
        <textarea
          className="tiv-textarea"
          value={draft.caption}
          onChange={(e) => setDraft({ ...draft, caption: e.target.value })}
          placeholder="why this dispatch belongs in the chronicle..."
          data-testid="terra-caption"
        />
      </label>

      <div>
        <div className="text-[0.6rem] tracking-[0.3em] uppercase text-[#555] mb-1">
          Tags (press enter or comma to add)
        </div>
        <div className="flex gap-2 mb-2">
          <input
            className="tiv-input flex-1"
            value={tagInput}
            onChange={(e) => setTagInput(e.target.value)}
            onKeyDown={onTagKey}
            placeholder="entity-name, layer-III, surface-bleed..."
            data-testid="terra-tag-input"
          />
          <button type="button" className="tiv-btn" onClick={addTag} data-testid="terra-tag-add">Affix</button>
        </div>
        <TagPills tags={draft.tags} onRemove={removeTag} />
      </div>

      <div>
        <div className="text-[0.6rem] tracking-[0.3em] uppercase text-[#555] mb-2">
          Plates ({(draft.images || []).length}/{MAX_IMAGES})
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {(draft.images || []).map((src, i) => (
            <div key={`${src.slice(-24)}-${i}`} className="border border-[#333] aspect-square overflow-hidden relative group">
              <img src={src} alt={`plate-${i}`} className="w-full h-full object-cover tiv-img" />
              <button
                onClick={() => removeImage(i)}
                className="absolute top-1 right-1 tiv-btn tiv-btn-danger text-[0.55rem] px-2 py-1 opacity-0 group-hover:opacity-100"
                data-testid={`terra-img-remove-${i}`}
              >
                Burn
              </button>
            </div>
          ))}
          {(draft.images || []).length < MAX_IMAGES && (
            <label className="border border-dashed border-[#333] aspect-square flex items-center justify-center cursor-crosshair hover:border-[#E3E1D9]" data-testid="terra-img-upload">
              <input type="file" accept="image/*" className="hidden" onChange={onUpload} />
              <span className="text-xs tracking-[0.3em] uppercase text-[#A3A3A3]">+ Affix</span>
            </label>
          )}
        </div>
      </div>

      <div className="flex gap-3 pt-2 border-t border-[#222]">
        <button className="tiv-btn" onClick={onSave} disabled={saving} data-testid="terra-save">
          {saving ? "Filing..." : editing ? "Seal Amendment" : "File Dispatch"}
        </button>
        <button className="tiv-btn" onClick={onCancel} data-testid="terra-cancel">Discard</button>
      </div>
    </div>
  );
}

const emptyDraft = () => ({ headline: "", caption: "", source_date: "", tags: [], images: [] });

function TagPills({ tags, onRemove }) {
  if (!tags || tags.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-2">
      {tags.map((t) => (
        <span key={t} className="inline-flex items-center gap-1 border border-[#333] bg-[#0a0a0a] px-2 py-1 text-[0.6rem] tracking-[0.25em] uppercase text-[#A3A3A3]">
          {t}
          {onRemove && (
            <button onClick={() => onRemove(t)} className="text-[#555] hover:text-[#A06A6A]" data-testid={`terra-tag-remove-${t}`}>×</button>
          )}
        </span>
      ))}
    </div>
  );
}

export default function TerraChronicle() {
  const { isAdmin, user } = useAuth();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [captures, setCaptures] = useState(null);
  const [allTags, setAllTags] = useState([]);
  const [composing, setComposing] = useState(false);
  const [editing, setEditing] = useState(null); // null | capture object
  const [draft, setDraft] = useState(emptyDraft());
  const [saving, setSaving] = useState(false);

  // Filters — initialised from URL query so deep-links like ?tag=layer-III work.
  const [filterTag, setFilterTag] = useState(searchParams.get("tag") || "");
  const [filterSince, setFilterSince] = useState(searchParams.get("since") || "");
  const [filterUntil, setFilterUntil] = useState(searchParams.get("until") || "");

  const reloadCaptures = useCallback((params = {}) => {
    return api.get("/terra-chronicle", { params })
      .then((r) => setCaptures(r.data))
      .catch((err) => {
        // Log so 5xx/network blips don't fail silently; UI still shows empty state.
        // Guarded so production builds don't leak request details to the browser console.
        if (err?.response?.status !== 401 && process.env.NODE_ENV !== "production") {
          console.error("[terra-chronicle] reload failed:", err?.message || err);
        }
        setCaptures([]);
      });
  }, []);

  const reloadTags = useCallback(() => {
    api.get("/terra-chronicle/tags")
      .then((r) => setAllTags(r.data))
      .catch(() => setAllTags([]));
  }, []);

  useEffect(() => {
    if (user === null) return;
    // Honor URL filters on every searchParams change (covers deep-links AND
    // in-app navigation to /the-terra-chronicle?tag=<x> from EntityDetail).
    const params = {};
    const t = searchParams.get("tag");
    const s = searchParams.get("since");
    const u = searchParams.get("until");
    if (t) params.tag = t;
    if (s) params.since = s;
    if (u) params.until = u;
    // Keep the visible filter inputs synced with the URL.
    setFilterTag(t || "");
    setFilterSince(s || "");
    setFilterUntil(u || "");
    reloadCaptures(params);
    reloadTags();
  }, [user, searchParams, reloadCaptures, reloadTags]);

  // If we navigated here from a detail-page "Amend" link, auto-open that
  // dispatch's edit form.
  useEffect(() => {
    if (!captures || !location.state?.openEditId) return;
    const found = captures.find((c) => c.id === location.state.openEditId);
    if (found) openEdit(found);
  }, [captures, location.state]);

  const applyFilters = () => {
    const params = {};
    if (filterTag) params.tag = filterTag;
    if (filterSince) params.since = filterSince;
    if (filterUntil) params.until = filterUntil;
    setSearchParams(params);
    reloadCaptures(params);
  };

  const clearFilters = () => {
    setFilterTag("");
    setFilterSince("");
    setFilterUntil("");
    setSearchParams({});
    reloadCaptures();
  };

  const openNew = () => {
    setEditing(null);
    setDraft(emptyDraft());
    setComposing(true);
  };

  const openEdit = (capture) => {
    setEditing(capture);
    setDraft({
      headline: capture.headline || "",
      caption: capture.caption || "",
      source_date: capture.source_date || "",
      tags: [...(capture.tags || [])],
      images: [...(capture.images || [])],
    });
    setComposing(true);
  };

  const cancel = () => {
    setComposing(false);
    setEditing(null);
    setDraft(emptyDraft());
  };

  const save = async () => {
    setSaving(true);
    try {
      if (editing) {
        const res = await api.put(`/terra-chronicle/${editing.id}`, draft);
        setCaptures((prev) => prev.map((c) => (c.id === editing.id ? res.data : c)));
        toast.success("Dispatch amended.");
      } else {
        const res = await api.post("/terra-chronicle", draft);
        setCaptures((prev) => [res.data, ...prev]);
        toast.success("Dispatch filed.");
      }
      reloadTags(); // tags may have changed
      cancel();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Filing failed.");
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (capture) => {
    if (!window.confirm(`Burn dispatch ${capture.id}?`)) return;
    try {
      await api.delete(`/terra-chronicle/${capture.id}`);
      setCaptures((prev) => prev.filter((c) => c.id !== capture.id));
      reloadTags();
      toast.success("Dispatch burned.");
    } catch {
      toast.error("Could not burn.");
    }
  };

  if (captures === null) {
    return <div className="text-[#555] text-xs tracking-widest uppercase">::: opening the chronicle :::</div>;
  }

  return (
    <div className="space-y-12" data-testid="terra-chronicle-page">
      <header className="space-y-4">
        <div className="text-[0.6rem] tracking-[0.4em] uppercase text-[#A3A3A3]">
          Capture Log · Surface Bleed
        </div>
        <h1 className="font-serif-display text-5xl sm:text-6xl tracking-tighter leading-none">
          The Terra Chronicle
        </h1>
        <p className="text-sm text-[#A3A3A3] max-w-2xl leading-relaxed">
          News dispatches filed from the surface world — articles intercepted, photographed,
          re-affixed to the archive. Read straight, not always for what they say, but for what
          they happen to confirm.
        </p>
        {isAdmin && !composing && (
          <div className="pt-3">
            <button className="tiv-btn" onClick={openNew} data-testid="terra-new-btn">
              + File New Dispatch
            </button>
          </div>
        )}
      </header>

      {composing && (
        <CaptureForm
          editing={editing}
          draft={draft}
          setDraft={setDraft}
          onSave={save}
          onCancel={cancel}
          saving={saving}
        />
      )}

      {/* Filter bar — visible to everyone past the Threshold */}
      <section className="border border-[#333] bg-[#0a0a0a] p-4" data-testid="terra-filters">
        <div className="grid grid-cols-1 sm:grid-cols-12 gap-3 items-end">
          <label className="sm:col-span-4">
            <div className="text-[0.6rem] tracking-[0.3em] uppercase text-[#555] mb-1">Tag</div>
            <select
              className="tiv-input"
              value={filterTag}
              onChange={(e) => setFilterTag(e.target.value)}
              data-testid="terra-filter-tag"
            >
              <option value="" className="bg-[#0a0a0a]">— all tags —</option>
              {/* If the URL tag has no matching dispatches it won't be in allTags;
                  surface it as a synthetic option so the user can see what's filtering. */}
              {filterTag && !allTags.includes(filterTag) && (
                <option key={`__active_${filterTag}`} value={filterTag} className="bg-[#0a0a0a]">
                  {filterTag} (0)
                </option>
              )}
              {allTags.map((t) => (
                <option key={t} value={t} className="bg-[#0a0a0a]">{t}</option>
              ))}
            </select>
          </label>
          <label className="sm:col-span-3">
            <div className="text-[0.6rem] tracking-[0.3em] uppercase text-[#555] mb-1">From</div>
            <input
              className="tiv-input"
              value={filterSince}
              onChange={(e) => setFilterSince(e.target.value)}
              placeholder="2026-01-01"
              data-testid="terra-filter-since"
            />
          </label>
          <label className="sm:col-span-3">
            <div className="text-[0.6rem] tracking-[0.3em] uppercase text-[#555] mb-1">Until</div>
            <input
              className="tiv-input"
              value={filterUntil}
              onChange={(e) => setFilterUntil(e.target.value)}
              placeholder="2026-12-31"
              data-testid="terra-filter-until"
            />
          </label>
          <div className="sm:col-span-2 flex gap-2">
            <button className="tiv-btn flex-1" onClick={applyFilters} data-testid="terra-filter-apply">Filter</button>
            <button className="tiv-btn" onClick={clearFilters} data-testid="terra-filter-clear">Reset</button>
          </div>
        </div>
      </section>

      {captures.length === 0 ? (
        <div className="border border-dashed border-[#333] p-16 text-center text-[#555] text-xs tracking-widest uppercase" data-testid="terra-empty">
          ::: no dispatches filed yet :::
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {captures.map((c) => (
            <CaptureCard
              key={c.id}
              capture={c}
              isAdmin={isAdmin}
              onEdit={openEdit}
              onDelete={onDelete}
            />
          ))}
        </div>
      )}

      <div className="border-t border-[#333] pt-6 text-[0.6rem] tracking-[0.4em] uppercase text-[#555] text-center">
        <Link to="/" className="hover:text-[#E3E1D9]">↩ Return to surface</Link>
      </div>
    </div>
  );
}
